// src/services/ga4Tracking.js

// Service de tracking des événements Google Analytics 4

/**
 * Fonction helper pour envoyer un événement GA4
 */
const sendEvent = (eventName, eventParams = {}) => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('GA4 non initialisé');
    return;
  }

  try {
    window.gtag('event', eventName, eventParams);
    console.log('📊 GA4 Event:', eventName, eventParams);
  } catch (error) {
    console.error('Erreur envoi événement GA4:', error);
  }
};

/**
 * Page view
 */
export const trackPageView = (pageName) => {
  sendEvent('page_view', {
    page_title: pageName,
    page_location: window.location.href,
    page_path: window.location.pathname
  });
};

/**
 * Début du processus de réservation
 */
export const trackBookingStarted = (language) => {
  sendEvent('booking_started', {
    language: language
  });
};

/**
 * Progression par étape
 */
export const trackStepProgress = (stepNumber, stepName, language) => {
  sendEvent('booking_step_progress', {
    step_number: stepNumber,
    step_name: stepName,
    language: language
  });
};

/**
 * Sélection des dates
 */
export const trackDatesSelected = (checkIn, checkOut, nights, language) => {
  sendEvent('dates_selected', {
    check_in: checkIn,
    check_out: checkOut,
    nights: nights,
    language: language
  });
};

/**
 * Sélection de chambre
 */
export const trackRoomSelected = (roomType, roomCount, occupancy, totalGuests, pricePerNight, language) => {
  sendEvent('room_selected', {
    room_type: roomType,
    room_count: roomCount,
    occupancy: occupancy,
    total_guests: totalGuests,
    price_per_night: pricePerNight,
    language: language
  });
};

/**
 * Ajout d'une option
 */
export const trackOptionAdded = (optionName, optionPrice, language) => {
  sendEvent('option_added', {
    option_name: optionName,
    option_price: optionPrice,
    language: language
  });
};

/**
 * Suppression d'une option
 */
export const trackOptionRemoved = (optionName, language) => {
  sendEvent('option_removed', {
    option_name: optionName,
    language: language
  });
};

/**
 * Formulaire d'informations complété
 */
export const trackGuestInfoCompleted = (hasCompany, country, language) => {
  sendEvent('guest_info_completed', {
    has_company: hasCompany,
    country: country,
    language: language
  });
};

/**
 * Code coupon appliqué
 */
export const trackCouponApplied = (couponCode, discountAmount, language) => {
  sendEvent('coupon_applied', {
    coupon_code: couponCode,
    discount_amount: discountAmount,
    language: language
  });
};

/**
 * CGV acceptées
 */
export const trackCGVAccepted = (language) => {
  sendEvent('cgv_accepted', {
    language: language
  });
};

/**
 * Paiement initié
 */
export const trackPaymentInitiated = (totalAmount, roomCount, nights, language) => {
  sendEvent('begin_checkout', {
    currency: 'EUR',
    value: totalAmount,
    items: [{
      item_name: 'Réservation',
      item_category: 'Hébergement',
      quantity: roomCount,
      price: totalAmount
    }],
    room_count: roomCount,
    nights: nights,
    language: language
  });
};

/**
 * Paiement réussi (conversion)
 */
export const trackPaymentSuccess = (
  reservationNumber, 
  totalAmount, 
  roomCount, 
  roomType,
  nights, 
  optionsCount,
  language
) => {
  // Événement de conversion principal
  sendEvent('purchase', {
    transaction_id: reservationNumber,
    currency: 'EUR',
    value: totalAmount,
    items: [{
      item_id: reservationNumber,
      item_name: 'Réservation',
      item_category: 'Hébergement',
      item_variant: roomType,
      quantity: roomCount,
      price: totalAmount
    }],
    room_count: roomCount,
    room_type: roomType,
    nights: nights,
    options_count: optionsCount,
    language: language
  });

  // Événement booking_completed spécifique
  sendEvent('booking_completed', {
    reservation_number: reservationNumber,
    total_amount: totalAmount,
    room_count: roomCount,
    room_type: roomType,
    nights: nights,
    options_count: optionsCount,
    language: language
  });
};

/**
 * Paiement échoué
 */
export const trackPaymentFailed = (errorReason, attemptNumber, totalAmount, language) => {
  sendEvent('payment_failed', {
    error_reason: errorReason,
    attempt_number: attemptNumber,
    value: totalAmount,
    currency: 'EUR',
    language: language
  });
};

/**
 * Réservation annulée après échec paiement
 */
export const trackReservationCancelled = (reason, totalAmount, language) => {
  sendEvent('reservation_cancelled', {
    reason: reason,
    value: totalAmount,
    currency: 'EUR',
    language: language
  });
};

/**
 * Retour à une étape précédente (abandon partiel)
 */
export const trackStepBack = (fromStep, toStep, language) => {
  sendEvent('step_back', {
    from_step: fromStep,
    to_step: toStep,
    language: language
  });
};

/**
 * Changement de langue
 */
export const trackLanguageChange = (newLanguage) => {
  sendEvent('language_change', {
    language: newLanguage
  });
};

/**
 * Visualisation des CGV
 */
export const trackCGVViewed = (language) => {
  sendEvent('cgv_viewed', {
    language: language
  });
};

/**
 * Erreur de disponibilité (aucune chambre)
 */
export const trackNoRoomsAvailable = (checkIn, checkOut, language) => {
  sendEvent('no_rooms_available', {
    check_in: checkIn,
    check_out: checkOut,
    language: language
  });
};
